import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form } from '@unform/web';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Select } from '../../../../components/Select';
import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { Pagination } from '../../../../components/Pagination';
import { SearchField } from '../../../../components/SearchField';
import {
  EmptyState,
  Table,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../../components/Table';

import {
  ActionsGroup,
  AddButton,
  Avatar,
  BillingBadge,
  Container,
  ExportButton,
  FiltersRow,
  GrowthBar,
  GrowthCard,
  GrowthChart,
  Header,
  HeaderActions,
  LastSession,
  LevelBadge,
  MemberCell,
  MemberName,
  MemberUsername,
  PageSubtitle,
  PageTitle,
  RowLink,
  SelectWrapper,
  StatBackgroundIcon,
  StatCard,
  StatLabel,
  StatsRow,
  StatusBadge,
  StatValue,
} from './styles';

import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';
import {
  labelLevelAccess,
  resolveAccessLevel,
} from '../../../../utils/labelLeverAccess';
import { downloadBlob } from '../../../../utils/downloadBlob';
import { useFeature } from '../../../../hooks/features';
import noAvatar from '../../../../assets/no-avatar.svg';

interface IUserProps {
  id: string;
  fullname: string;
  username: string;
  email?: string;
  permissions: string[];
  avatar_url: string | undefined;
  is_active?: boolean;
  membership_status?: string;
  lastTraining?: string;
  billingStatus?: {
    status: 'ok' | 'pending' | 'overdue' | 'not_informed';
    days_overdue?: number;
  };
}

const billingLabel: Record<string, string> = {
  ok: 'Em dia',
  pending: 'Aguardando',
  not_informed: 'Não informado',
};

interface IGrowthItem {
  label: string;
  value: number;
}

interface IDashboardProps {
  usersGrowth: IGrowthItem[];
  usersRegisteredThisMonth: number;
}

// "Aluno(a)" não tem filtro dedicado — o backend só filtra por permissão
// presente (`permission_code`), não por "nenhuma permissão".
const accessLevel = [
  { value: '', label: 'Todos(as)' },
  { value: 'view_gym_dashboard', label: 'Funcionário(a)' },
  { value: 'manage_gym_users', label: 'Administrador(a)' },
];

const trainingExpirationOptions = [
  { value: '', label: 'Todos(as)' },
  { value: 'expired_or_expiring', label: 'Expirado(a) ou expirando' },
  { value: 'expired', label: 'Expirado(a)' },
  { value: 'expiring', label: 'Expirando' },
];

const PAGE_SIZE = 10;

interface IUsersProps {
  variant?: 'full' | 'trainings';
}

const Users: React.FC<IUsersProps> = ({ variant = 'full' }) => {
  const isTrainingsVariant = variant === 'trainings';
  const basePath = isTrainingsVariant ? '/trainings' : '/users';

  const navigate = useNavigate();
  // Lido uma vez só na montagem -- deixa o indicador clicável do
  // dashboard ("Fichas expirando") linkar direto pra cá com o filtro já
  // aplicado, via `?training_expiration=...` na URL.
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<IUserProps[]>([]);
  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [select, setSelect] = useState('');
  const [trainingExpirationFilter, setTrainingExpirationFilter] = useState(
    () => searchParams.get('training_expiration') || '',
  );
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dashboard, setDashboard] = useState<IDashboardProps>();
  const hasExportData = useFeature('export_data');
  const [exportingKey, setExportingKey] = useState<'alunos' | 'treinos' | null>(
    null,
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, select, trainingExpirationFilter]);

  useEffect(() => {
    setLoading(true);
    api
      .get('users/gym', {
        params: {
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          permission_code: select || undefined,
          training_expiration: trainingExpirationFilter || undefined,
        },
      })
      .then(response => {
        setUsers(response?.data.data);
        setTotal(response?.data.total);
        setTotalPages(response?.data.totalPages);
        setStats(response?.data.stats);
      })
      .catch(err => console.log(err?.response?.status))
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, select, trainingExpirationFilter]);

  useEffect(() => {
    if (isTrainingsVariant) return;

    api
      .get('gyms/dashboard')
      .then(response => setDashboard(response?.data))
      .catch(err => console.log(err?.response?.status));
  }, [isTrainingsVariant]);

  const handleChangeSelectForm = useCallback((e: any) => {
    setSelect(e.value);
  }, []);

  const handleChangeTrainingExpirationFilter = useCallback((e: any) => {
    setTrainingExpirationFilter(e.value);
  }, []);

  const handleExport = useCallback(
    async (key: 'alunos' | 'treinos', endpoint: string, filename: string) => {
      if (!hasExportData || exportingKey) {
        return;
      }

      setExportingKey(key);

      try {
        const response = await api.get(endpoint, { responseType: 'blob' });
        downloadBlob(response.data, filename);
      } catch (err) {
        console.log(err);
      } finally {
        setExportingKey(null);
      }
    },
    [hasExportData, exportingKey],
  );

  const growthBars = useMemo(() => {
    const items = dashboard?.usersGrowth || [];
    const max = Math.max(1, ...items.map(item => item.value));

    return items.map((item, index) => ({
      ...item,
      height: (item.value / max) * 100,
      filled: index === items.length - 1,
    }));
  }, [dashboard]);

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <div>
            <PageTitle>
              {isTrainingsVariant ? 'Fichas de Treino' : 'Diretório de Alunos'}
            </PageTitle>
            <PageSubtitle>
              {isTrainingsVariant
                ? 'Selecione um aluno para montar ou editar o treino'
                : `Gerenciando ${stats.total} alunos de alta performance`}
            </PageSubtitle>
          </div>
          {!isTrainingsVariant && (
            <HeaderActions>
              <ExportButton
                type="button"
                locked={!hasExportData}
                disabled={!!exportingKey}
                onClick={() =>
                  handleExport('alunos', 'users/gym/export', 'alunos.csv')
                }
                title={
                  !hasExportData
                    ? 'Disponível a partir do plano Performance'
                    : undefined
                }
              >
                <Icon
                  name={hasExportData ? 'download' : 'lock'}
                  size={18}
                  color={colors.onSurface}
                />
                {exportingKey === 'alunos'
                  ? 'Exportando...'
                  : 'Exportar alunos'}
              </ExportButton>
              <ExportButton
                type="button"
                locked={!hasExportData}
                disabled={!!exportingKey}
                onClick={() =>
                  handleExport('treinos', 'trainings/gym/export', 'treinos.csv')
                }
                title={
                  !hasExportData
                    ? 'Disponível a partir do plano Performance'
                    : undefined
                }
              >
                <Icon
                  name={hasExportData ? 'download' : 'lock'}
                  size={18}
                  color={colors.onSurface}
                />
                {exportingKey === 'treinos'
                  ? 'Exportando...'
                  : 'Exportar treinos'}
              </ExportButton>
              <AddButton type="button" onClick={() => navigate('/users/new')}>
                <Icon name="person_add" size={18} color={colors.onPrimary} />
                Adicionar Aluno
              </AddButton>
            </HeaderActions>
          )}
        </Header>

        {!isTrainingsVariant && (
          <>
            <StatsRow>
              <StatCard>
                <StatLabel>Total Ativos</StatLabel>
                <StatValue tone="primary">{stats.active}</StatValue>
                <StatBackgroundIcon>
                  <Icon name="bolt" size={96} color={colors.primary} />
                </StatBackgroundIcon>
              </StatCard>

              <GrowthCard>
                <div>
                  <StatLabel>Crescimento de Alunos</StatLabel>
                  <StatValue>
                    {dashboard?.usersRegisteredThisMonth ?? '—'}{' '}
                    <span>este mês</span>
                  </StatValue>
                </div>
                <GrowthChart>
                  {growthBars.map(bar => (
                    <GrowthBar
                      key={bar.label}
                      height={bar.height}
                      filled={bar.filled}
                      title={`${bar.label}: ${bar.value} aluno(s)`}
                    />
                  ))}
                </GrowthChart>
              </GrowthCard>
            </StatsRow>

            <Form onSubmit={() => undefined}>
              <FiltersRow>
                <SearchField
                  placeholder="Buscar alunos, atividades..."
                  value={searchText}
                  onChange={setSearchText}
                />
                <SelectWrapper>
                  <Select
                    name="select"
                    options={accessLevel}
                    placeholder="Nível de acesso"
                    onChange={handleChangeSelectForm}
                    blurInputOnSelect
                  />
                </SelectWrapper>
              </FiltersRow>
            </Form>
          </>
        )}

        {isTrainingsVariant && (
          <Form
            onSubmit={() => undefined}
            initialData={{
              training_expiration: trainingExpirationOptions.find(
                option => option.value === trainingExpirationFilter,
              ),
            }}
          >
            <FiltersRow>
              <SearchField
                placeholder="Buscar alunos, atividades..."
                value={searchText}
                onChange={setSearchText}
              />
              <SelectWrapper>
                <Select
                  name="training_expiration"
                  options={trainingExpirationOptions}
                  placeholder="Expiração da ficha"
                  onChange={handleChangeTrainingExpirationFilter}
                  blurInputOnSelect
                />
              </SelectWrapper>
            </FiltersRow>
          </Form>
        )}

        <TableWrapper>
          <Table>
            <Thead>
              <tr>
                <Th>Aluno</Th>
                <Th>Status</Th>
                <Th>Nível de acesso</Th>
                <Th>Financeiro</Th>
                <Th>Última Sessão</Th>
                <Th>Ações</Th>
              </tr>
            </Thead>
            <Tbody>
              {users.map(userCurrent => {
                const levelLabel = labelLevelAccess(userCurrent.permissions);

                return (
                  <tr key={userCurrent.id}>
                    <Td>
                      <MemberCell>
                        <Avatar
                          src={userCurrent.avatar_url || noAvatar}
                          alt={userCurrent.fullname}
                        />
                        <div>
                          <MemberName>{userCurrent.fullname}</MemberName>
                          <MemberUsername>
                            {userCurrent.email || `@${userCurrent.username}`}
                          </MemberUsername>
                        </div>
                      </MemberCell>
                    </Td>
                    <Td>
                      <StatusBadge
                        status={
                          userCurrent.is_active === false
                            ? 'inactive'
                            : userCurrent.membership_status === 'pending'
                            ? 'pending'
                            : 'active'
                        }
                      >
                        {userCurrent.is_active === false
                          ? 'Inativo'
                          : userCurrent.membership_status === 'pending'
                          ? 'Pendente'
                          : 'Ativo'}
                      </StatusBadge>
                    </Td>
                    <Td>
                      <LevelBadge
                        level={resolveAccessLevel(userCurrent.permissions)}
                      >
                        {levelLabel}
                      </LevelBadge>
                    </Td>
                    <Td>
                      {userCurrent.billingStatus ? (
                        <BillingBadge
                          status={userCurrent.billingStatus.status}
                          title="Situação financeira"
                        >
                          {userCurrent.billingStatus.status === 'overdue'
                            ? `Atrasado (${userCurrent.billingStatus.days_overdue}d)`
                            : billingLabel[userCurrent.billingStatus.status]}
                        </BillingBadge>
                      ) : (
                        <span>—</span>
                      )}
                    </Td>
                    <Td>
                      {userCurrent.lastTraining ? (
                        <LastSession>
                          <span>
                            {format(
                              new Date(userCurrent.lastTraining),
                              'dd/MM/yyyy',
                            )}
                          </span>
                          <span>
                            há{' '}
                            {formatDistanceToNow(
                              new Date(userCurrent.lastTraining),
                              { locale: ptBR },
                            )}
                          </span>
                        </LastSession>
                      ) : (
                        <LastSession title="Nenhum treino concluído registrado">
                          <span>—</span>
                          <span>Sem dados</span>
                        </LastSession>
                      )}
                    </Td>
                    <Td>
                      {isTrainingsVariant ? (
                        <RowLink
                          type="button"
                          onClick={() =>
                            navigate(`${basePath}/${userCurrent.id}/0`)
                          }
                        >
                          <Icon
                            name="chevron_right"
                            size={20}
                            color={colors.onSurfaceVariant}
                          />
                        </RowLink>
                      ) : (
                        <ActionsGroup>
                          <RowLink
                            type="button"
                            title="Ficha de treino"
                            onClick={() =>
                              navigate(`/users/${userCurrent.id}/0`)
                            }
                          >
                            <Icon
                              name="assignment"
                              size={18}
                              color={colors.onSurfaceVariant}
                            />
                          </RowLink>
                          <RowLink
                            type="button"
                            title="Medidas"
                            onClick={() =>
                              navigate(`/users/${userCurrent.id}/measurements`)
                            }
                          >
                            <Icon
                              name="straighten"
                              size={18}
                              color={colors.onSurfaceVariant}
                            />
                          </RowLink>
                          <RowLink
                            type="button"
                            title="Mensalidade"
                            onClick={() =>
                              navigate(`/users/${userCurrent.id}/dues`)
                            }
                          >
                            <Icon
                              name="payments"
                              size={18}
                              color={colors.onSurfaceVariant}
                            />
                          </RowLink>
                          <RowLink
                            type="button"
                            title="Detalhes"
                            onClick={() => navigate(`/users/${userCurrent.id}`)}
                          >
                            <Icon
                              name="chevron_right"
                              size={20}
                              color={colors.onSurfaceVariant}
                            />
                          </RowLink>
                        </ActionsGroup>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </Tbody>
          </Table>

          {!users.length && <EmptyState>Nenhum aluno encontrado.</EmptyState>}
        </TableWrapper>

        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={PAGE_SIZE}
          itemLabel="alunos"
          onPageChange={setPage}
        />
      </Container>
    </Painel>
  );
};

export { Users };
